import BuildInstallButton from '@/components/build/build-install-button';
import BuildCancelButton from '@/components/build/build-cancel-button';
import BuildLogsSection from '@/components/build/build-logs-section';
import BuildStatusBadge from '@/components/build/build-status-badge';
import { getBuilderProjectLabel } from '@/api/builder-projects';
import type { IBuild } from '@/entities/build.entity';
import { isActiveBuild } from '@/entities/build.entity';
import { cn } from '@/lib/utils';
import { BoxIcon, CalendarIcon } from 'lucide-react';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);

interface BuildCardProps {
	build: IBuild;
	className?: string;
	onCancel?: () => void;
	adminLogs?: boolean;
}

export default function BuildCard({ build, className, onCancel, adminLogs = false }: BuildCardProps) {
	const title = build.appName || build.id;
	const projectLabel = getBuilderProjectLabel(build.androidProjectDir);
	const showInstall = Boolean(build.successBuilded);
	const showCancel = isActiveBuild(build) && build.canCancel;
	const showFailureLogs = Boolean(build.hasBuildLog) && !isActiveBuild(build);

	return (
		<div className={cn('rounded-xl border border-border bg-card p-3 shadow-sm', className)}>
			<div className='flex items-start gap-3'>
				<div className='relative size-12 shrink-0 overflow-hidden rounded-xl border border-border bg-muted/40'>
					{build.appIcon ? (
						<img src={build.appIcon} alt={title} className='size-full object-cover' />
					) : (
						<div className='flex size-full items-center justify-center text-muted-foreground'>
							<BoxIcon size={20} />
						</div>
					)}
				</div>

				<div className='min-w-0 flex-1'>
					<div className='flex items-start justify-between gap-2'>
						<div className='min-w-0'>
							<div className='flex items-center gap-2'>
								<p className='truncate font-medium'>{title}</p>
								{build.testBuild ? (
									<span className='inline-flex shrink-0 items-center rounded-md border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground'>
										DEBUG
									</span>
								) : null}
								{build.skipEncrypt ? (
									<span className='inline-flex shrink-0 items-center rounded-md border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground'>
										TEST
									</span>
								) : null}
								{projectLabel ? (
									<span className='inline-flex shrink-0 items-center rounded-md border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground'>
										{projectLabel}
									</span>
								) : null}
							</div>
							<p className='truncate text-xs text-muted-foreground'>#{build.id}</p>
						</div>
						<BuildStatusBadge build={build} />
					</div>

					<div className='mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground'>
						<div className='flex items-center gap-1.5'>
							<CalendarIcon size={12} className='opacity-50' />
							<TimeAgo date={build.createdAt} formatter={formatter} />
						</div>
						<span>{build.clientsCount} клиентов</span>
					</div>
				</div>
			</div>

			{showInstall || showCancel || showFailureLogs ? (
				<div className='mt-3 flex flex-col gap-3 border-t border-border/60 pt-3'>
					{showFailureLogs ? <BuildLogsSection build={build} admin={adminLogs} /> : null}
					{showInstall || showCancel ? (
						<div className='flex items-center justify-between gap-2'>
							{showCancel ? <BuildCancelButton build={build} onCancelled={onCancel} /> : <span />}
							{showInstall ? <BuildInstallButton build={build} /> : null}
						</div>
					) : null}
				</div>
			) : null}
		</div>
	);
}
